import React, { useContext } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { Box, Flex } from '@components/layout/Grid';
import {
  CardDescription,
  CardDescriptionDivStyledP,
  CardTitle,
} from '@components/texts';
import ThumbnailWithRatio from '@components/ThumbnailWithRatio';
import LanguageContext from '@lib/contexts/languageContext';

const Wrapper = styled.div<{ borderless?: boolean }>`
  border: ${props => (props.borderless ? 'none' : '1px solid #eeeeee')};
  border-radius: 5px;
  background-color: #ffffff;
  overflow: hidden;

  a {
    text-decoration: none;
  }
`;

const DescriptionWrapper = styled(Box)``;

const RedirectTo = props => {
  let params = props.redirectTo;
  if (typeof props.redirectTo === 'string') {
    params = { href: props.redirectTo };
  }

  return (
    <Link {...params}>
      {props.children}
    </Link>
  );
};

const ThumbnailSection = props => {
  if (props.redirectTo) {
    return (
      <RedirectTo redirectTo={props.redirectTo} locale={props.locale}>
        {props.thumbnailUrl ? (
          <ThumbnailWithRatio
            large={props.large}
            path={props.thumbnailUrl}
            withRadius
          >
            {props.thumbnailContent}
          </ThumbnailWithRatio>
        ) : (
          <ThumbnailWithRatio
            large={props.large}
            path="/static/playlist-empty-ic.svg"
            withRadius
          >
            {props.thumbnailContent}
          </ThumbnailWithRatio>
        )}
      </RedirectTo>
    );
  }

  if (props.href) {
    return (
      <a href={props.href}>
        <ThumbnailWithRatio
          large={props.large}
          path={props.thumbnailUrl}
          withRadius
        >
          {props.thumbnailContent}
        </ThumbnailWithRatio>
      </a>
    );
  }

  return (
    <ThumbnailWithRatio
      large={props.large}
      path={props.thumbnailUrl}
      withRadius
    >
      {props.thumbnailContent}
    </ThumbnailWithRatio>
  );
};

const TitleSection = props => {
  if (props.redirectTo) {
    return (
      <RedirectTo redirectTo={props.redirectTo} locale={props.locale}>
        <CardTitle>{props.contentTitle}</CardTitle>
      </RedirectTo>
    );
  }
  if (props.href) {
    return (
      <a href={props.href}>
        <CardTitle>{props.contentTitle}</CardTitle>
      </a>
    );
  }
  return <CardTitle>{props.contentTitle}</CardTitle>;
};

type Props = {
  className?: string;
  large?: boolean;
  borderless?: boolean;
  onClick?(): void;
  href?: string;
  redirectTo?: string | { href: string };
  thumbnailContent: React.ReactNode;
  thumbnailUrl: string;
  contentTitle: string;
  contentDescription: string;
  contentAuthor?: React.ReactNode;
  isContentDescriptionHTML?: boolean;
  contentCardLeftSection?: React.ReactNode;
  contentCardRightSection?: React.ReactNode;
  contentRightCardLargeSection?: React.ReactNode;
  contentLeftCardLargeSection?: React.ReactNode[];
};

const Card = (props: Props) => {
  const locale = useContext(LanguageContext);

  return props.large ? (
    <Wrapper
      className={props.className}
      borderless={props.borderless}
      data-testid={props['data-testid']}
      data-test-type={props['data-test-type']}
    >
      <Flex>
        <Box flex="0 0 407px" onClick={props.onClick}>
          <ThumbnailSection
            href={props.href}
            redirectTo={props.redirectTo ? props.redirectTo : null}
            thumbnailContent={props.thumbnailContent}
            thumbnailUrl={props.thumbnailUrl}
            locale={locale}
            large={true}
          />
        </Box>
        <Box flex="1" px="25px" py="20px">
          <Flex
            flexDirection="column"
            justifyContent="space-between"
            style={{ height: '100%' }}
          >
            <Box>
              <Box mb="10px" onClick={props.onClick} data-testid="Card--Title">
                <TitleSection
                  contentTitle={props.contentTitle}
                  redirectTo={props.redirectTo}
                  locale={locale}
                />
              </Box>
              {props.contentAuthor ? (
                <Box mb="10px">
                  <CardDescription>{props.contentAuthor}</CardDescription>
                </Box>
              ) : null}
              <Box mb="10px" data-testid="Card--Description">
                {props.isContentDescriptionHTML ? (
                  <CardDescription
                    dangerouslySetInnerHTML={{
                      __html: props.contentDescription,
                    }}
                  />
                ) : (
                  <CardDescription>{props.contentDescription}</CardDescription>
                )}
              </Box>
            </Box>
            <Box>
              <Flex alignItems="center" justifyContent="space-between">
                <Box>
                  <Flex mx="-10px">
                    {props.contentLeftCardLargeSection.map((item, index) => (
                      <Box key={index} px="10px">
                        {item}
                      </Box>
                    ))}
                  </Flex>
                </Box>
                <Box>{props.contentRightCardLargeSection}</Box>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Flex>
    </Wrapper>
  ) : (
    <Wrapper
      className={props.className}
      borderless={props.borderless}
      data-testid={props['data-testid']}
      data-test-type={props['data-test-type']}
    >
      <Box onClick={props.onClick}>
        <ThumbnailSection
          href={props.href}
          redirectTo={props.redirectTo}
          thumbnailContent={props.thumbnailContent}
          thumbnailUrl={props.thumbnailUrl}
          locale={locale}
        />
      </Box>
      <Box px="25px" py="20px">
        <Box mb="10px" onClick={props.onClick}>
          <TitleSection
            href={props.href}
            contentTitle={props.contentTitle}
            redirectTo={props.redirectTo}
            locale={locale}
          />
        </Box>
        {props.contentAuthor ? (
          <Box mb="10px">
            <CardDescription>{props.contentAuthor}</CardDescription>
          </Box>
        ) : null}
        <DescriptionWrapper mb="10px">
          {props.isContentDescriptionHTML ? (
            <CardDescriptionDivStyledP
              dangerouslySetInnerHTML={{
                __html: props.contentDescription,
              }}
            />
          ) : (
            <CardDescription>{props.contentDescription}</CardDescription>
          )}
        </DescriptionWrapper>
        <Flex alignItems="center" justifyContent="space-between">
          <Box>{props.contentCardLeftSection}</Box>
          <Box>{props.contentCardRightSection}</Box>
        </Flex>
      </Box>
    </Wrapper>
  );
};

Card.toString = Wrapper.toString;
export default Card;
